import { Check, SealPercent } from "@phosphor-icons/react";
import DotsLoader from "components/common/loaders/DotsLoader";
import { Icon } from "@shopflo/ui/icon";
import { getTextColorByBrightness } from "@shopflo/ui/utils";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { CouponType } from "lib/types/checkout";
import { currencyFormatter } from "lib/utils/formatters";
import React from "react";
import ViewOtherCouponsTile from "./ViewCouponsTile";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { CouponRemovableTileProps } from "../couponProps";
import MultiGiftCard from "../cards/MultiGiftCard";
import GiftCard from "../cards/GiftCard";
import Gyftr from "../cards/Gyftr";
const CouponRemovableTile: React.FC<CouponRemovableTileProps> = ({
  removableAppliedCoupon,
  setCouponApplyError,
  showCoupons,
  availableCoupons,
  handleDeleteCoupon,
  renderCouponSummaryStrip,
  customisations,
  onOpenPartnerOffers,
  onOpen,
  isLoading = false,
  partnerOffersConfig,
}) => {
  const {
    state: { appliedCoupons, coupons, shippingHandles },
    actions: { setCheckoutModal },
  } = useCheckoutContext();
  const { t } = useLocale();
  if (!removableAppliedCoupon) return null;

  const selectedShippingHandle = shippingHandles?.find((handle) => handle.selected_handle);

  const isBankOfferApplied = appliedCoupons?.some((coupon) => coupon.couponType === CouponType.PAYMENT_OFFER);
  const isShippingCouponApplied = appliedCoupons?.some((coupon) => coupon.couponType === CouponType.SHIPPING);

  const { isReward, couponRewardConfig, rewardData, discountValue, title, code, isRemovable } =
    removableAppliedCoupon;

  const showRewardHeader = isReward && couponRewardConfig?.walletType === "TICKERTAPE";
  const displayText = showRewardHeader
    ? rewardData?.header
    : `${currencyFormatter(discountValue)} ${t("savings")}`;

  const couponTitle = title || code;

  const renderDiscountRemovableContent = () => {
    if (isBankOfferApplied) {
      return <p className="text-text-sm-medium">{t("bank_offer_applied")}</p>;
    }

    if (isShippingCouponApplied && selectedShippingHandle) {
      const originalPrice = parseFloat(selectedShippingHandle?.original_price || "0");
      const currentPrice = parseFloat(selectedShippingHandle?.price || "0");
      const discountAmount = originalPrice - currentPrice;

      const formattedPrice = currencyFormatter(discountAmount);

      const shippingDiscountText = `${formattedPrice} ${t("savings")}`;

      return (
        <>
          <p className="text-text-sm-medium">{shippingDiscountText}</p>
          <p className="text-text-xs-semibold">
            <span className="text-coal-light mr-1"
              style={{
                color: customisations?.callout?.infoColor,
                opacity: 0.8,
              }}
            >{t("with")}</span>
            <span className="text-coal-dark"
              style={{
                color: customisations?.callout?.infoColor,
              }}
            >{"'" + couponTitle + "'"}</span>
            <Check className="h-3.5 w-3.5 ml-1 inline align-middle mb-0.5" strokeWidth={2.75}
              style={{
                color: customisations?.callout?.infoColor,
              }}
            />
          </p>
        </>
      );
    }
    return (
      <>
        <p className="text-text-sm-medium">{displayText}</p>
        <p className="text-text-xs-semibold">
          <span className="text-coal-light mr-1"
            style={{
              color: customisations?.callout?.infoColor,
              opacity: 0.8,
            }}
          >{t("with")}</span>
          <span className="text-coal-dark"
            style={{
              color: customisations?.callout?.infoColor,
            }}
          >{"'" + couponTitle + "'"}</span>
          <Check className="h-3.5 w-3.5 ml-1 inline align-middle mb-0.5" strokeWidth={2.75}
            style={{
              color: customisations?.callout?.infoColor,
            }}
          />
        </p>
      </>
    );
  };


  return (
    <div className="isolate flex w-full flex-col overflow-hidden rounded-xl border border-green-dark/20 text-yay-dark"
      style={{
        ...getCardBorderStyles({
          borderSides: customisations?.cardBorderSides,
          borderWidth: customisations?.cardBorderWidth,
          borderColor: customisations?.callout.borderColor,
        }),
        borderRadius: customisations?.cardRadius ?? 12,
      }}
    >
      <div className="flex flex-col cursor-pointer">
        {showCoupons && (
          <div
            className="flex justify-between items-center p-3"
            onClick={() => {
              setCheckoutModal("DISCOUNT_LIST");
              setCouponApplyError(() => ({ status: false, message: "" }));
            }}
            style={{
              backgroundColor: customisations?.callout?.backgroundColor,
              color: customisations?.callout?.primaryColor,
            }}
          >
            <div className="flex gap-1">
              <Icon IconComponent={SealPercent} size="sm" iconProps={{ weight: "fill" }}></Icon>

              <div className="flex flex-col space-y-[3px] justify-start items-start">
                {renderDiscountRemovableContent()}
              </div>
            </div>
            {isRemovable ? (
              <div
                onClick={(e) => {
                  if (isLoading) return;
                  e.stopPropagation();
                  handleDeleteCoupon(code);
                }}
                className="flex z-20 justify-center px-3 py-1.5 items-center h-full rounded-md cursor-pointer min-w-[52px]">
                {isLoading ? (
                  <DotsLoader color={customisations?.callout?.primaryColor ?? "#2C874A"} />
                ) : (
                  <span className="text-text-xs-semibold">{t("remove")}</span>
                )}
              </div>
            ) : (
              <div
                onClick={(e) => e.stopPropagation()}
                className="flex z-20 justify-center px-3 py-1.5 items-center h-full rounded-md cursor-pointer min-w-[52px]">
                {isLoading ? (
                  <DotsLoader color={customisations?.callout?.primaryColor ?? "#2C874A"} />
                ) : (
                  <span className="text-text-xs-semibold">{t("coupon_applied")}</span>
                )}
              </div>
            )}
          </div>
        )}

        {renderCouponSummaryStrip()}
        <div className="flex flex-col border-t py-0.5"
          style={{
            backgroundColor: customisations?.footer?.backgroundColor,
            borderColor: getTextColorByBrightness({ bgHexColor: customisations?.footer?.backgroundColor ?? '2C874A', lightOpacity: 24 }),
          }}
        >
          <ViewOtherCouponsTile
            availableCoupons={availableCoupons}
            setCouponApplyError={setCouponApplyError}
            customisations={customisations}
            onOpenPartnerOffers={onOpenPartnerOffers}
            onOpen={onOpen}
            partnerOffersConfig={partnerOffersConfig}
          />
           <GiftCard dividerColor={getTextColorByBrightness({ bgHexColor: customisations?.footer?.backgroundColor ?? '2C874A', lightOpacity: 24 })} />
           <Gyftr dividerColor={getTextColorByBrightness({ bgHexColor: customisations?.footer?.backgroundColor ?? '2C874A', lightOpacity: 24 })} />
          <MultiGiftCard dividerColor={getTextColorByBrightness({ bgHexColor: customisations?.footer?.backgroundColor ?? '2C874A', lightOpacity: 24 })} />
        </div>
      </div>
    </div>
  );
};

export default CouponRemovableTile;
